import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';

import T, { Translate } from '../../i18n/i18n';
import * as Tag from './ProfileForm.styles';
import VisibilityChanger from '../../Forms/VisibilityChanger/VisibilityChanger';
import Checkbox from '../../Forms/Checkbox/Checkbox';
import TagSelector from '../../Forms/TagSelector/TagSelector';
import DateInput from '../../Forms/DateInput/DateInput';
import TimeSelector from '../../Forms/TimeSelector/TimeSelector';
import { YearDiff, ConvertToTimeDisplay } from '../../../services/dates';
import { SanitizeUsername } from '../../../services/strings';
import { HEXtoBlackOrWhite } from '../../../services/colors';
import { configActions, authActions } from '../../../redux/actions';
import { editProfile, getMyProfile } from '../../../services/profile';

const statesList = ["", "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

const initialFormState = {
    name: "",
    username: "",
    birthdate: "",
    age: "-",
    gender: "",
    email: "",
    city: "",
    state: "",
    color: "",
    bio: "",
    acceptVoice: false,
    acceptVideo: false,
    acceptStream: false,
    hasDesktop: false,
    hasTablet: false,
    hasPhone: false,
    whatsapp: "",
    telegram: "",
    facebook: "",
    instagram: "",
    twitter: "",
    discord: "",
    player_acceptInvite: false,
    player_contentLikable: [],
    player_contentAvoidable: [],
    player_systems: [],
    player_tools: [],
    gm_acceptInvite: false,
    gm_contentLikable: [],
    gm_contentAvoidable: [],
    gm_systems: [],
    gm_tools: [],

    view_age:"H",
    view_gender:"H",
    view_address:"H",
    view_whatsapp: "H",
    view_telegram: "H",
    view_facebook: "H",
    view_instagram: "H",
    view_twitter: "H",
    view_discord: "H",
};

const initialFormAvailability = {
    id: "", 
    weekday:"", 
    timestart:"", 
    timeend:"", 
    frequency:"", 
    note:""
};

const displayableAvailability = (data) => {
    const result = [...data];

    for (let i in result) {
        result[i].timestart = ConvertToTimeDisplay(result[i].timestart);
        result[i].timeend = ConvertToTimeDisplay(result[i].timeend);
    }

    return result;
}

export default () => {

    const history = useHistory();
    const dispatch = useDispatch();
    const lang = useSelector(state => state.configState.lang);
    
    const [data, setData] = useState(initialFormState);
    const [availability, setAvailability] = useState([]);
    const [availabilityForm, setAvailabilityForm] = useState(initialFormAvailability);
    const [selectedAvailability, setSelectedAvailability] = useState(null);
    const [error, setError] = useState(null);
    const [tabSelected, setTabSelected] = useState('player');
    
    const handleBack = () => {
        history.goBack();
    };

    const setProfileData = (key, value) => {
        setData(d => ({...d, [key]: value}));
    }

    const setAvailabilityData = (key, value) => {
        setAvailabilityForm(d => ({...d, [key]: value}));
    }

    const handleBirthdateChange = useCallback((v, dt) => {
        setProfileData("birthdate", v);
        setProfileData("age", YearDiff(dt));
    }, []);

    const isValidAvailability = () => {
        return availabilityForm.weekday !== '' &&
            availabilityForm.timestart !== '' &&
            availabilityForm.timeend !== '' &&
            availabilityForm.frequency !== '';
    }

    const handleSaveAvailability = () => {
        if (!isValidAvailability()) {
            return;
        }

        let item = {...availabilityForm};

        if (!selectedAvailability) {
            item.id = availability.map(a=>a.id).reduce((acc,cur)=>Math.max(cur, acc),0) + 1;
            setAvailability(av => [...av, item]);
        } else {
            setAvailability(av => av.map(a => a.id !== selectedAvailability ? a : item));
        }

        setAvailabilityForm(initialFormAvailability);
        setSelectedAvailability(null);
    }

    const handleRemoveAvailability = (id) => {
        setAvailability(a => a.filter(i => i.id !== id));
        setAvailabilityForm(initialFormAvailability);
        setSelectedAvailability(null);
    }

    const handleSelectAvailability = (availability) => {
        if (selectedAvailability === availability.id) {
            setAvailabilityForm(initialFormAvailability);
            setSelectedAvailability(null);
        } else {
            setAvailabilityForm(availability);
            setSelectedAvailability(availability.id);
        }
    }

    const handleSave = async () => {
        let user = await editProfile({...data, availability});
        if (user.status === "ok") {
            dispatch(authActions.login(user.payload));
            handleBack();
        } else {
            setError(user.payload || Translate('undefinederror'));
        }
    }

    const handleSetLang = (lang) => {
        dispatch(configActions.changeLang(lang));
    }

    const fgColor = useMemo(() => {
        return HEXtoBlackOrWhite(data.color);
    }, [data.color]);

    useEffect(()=>{
        (async () => {
            let data = await getMyProfile();
            if (data.status === 'ok') {
                let dt = {};
                for(let f of Object.keys(initialFormState)) {
                    dt[f] = data.payload[f];
                }
                setData(dt);
                setAvailability(displayableAvailability(data.payload.availability))
            }
        })();
    }, []);

    return <>
        <Tag.HeaderRow>
            <Tag.ButtonBack color="#aaa" onClick={handleBack} />
            <Tag.Title><T value="profileform.title" /></Tag.Title>
        </Tag.HeaderRow>

        <Tag.Content>
            {error ? <Tag.Error>{error}</Tag.Error> : null }
            <Tag.Row justify="flex-start">
                <Tag.Image color={data.color} fgColor={fgColor}>{String(data.name ?? '').substring(0,1).toUpperCase()}</Tag.Image>    
                <Tag.Column className="noFlex mr">
                    <Tag.Field>
                        <Tag.Label><T value="profileform.name" /></Tag.Label>
                        <Tag.Input style={{width:"230px"}} value={data.name} onChange={(e) => setProfileData("name", e.target.value)} />
                    </Tag.Field>
                    <Tag.Field>
                        <Tag.Label><T value="profileform.email" /></Tag.Label>
                        <Tag.Value>{data.email}</Tag.Value>
                    </Tag.Field>
                </Tag.Column>
                <Tag.Column className="noFlex mr">
                    <Tag.Row align="flex-end">
                        <Tag.Field>
                            <Tag.Label><T value="profileform.gender" /></Tag.Label>
                            <Tag.Select style={{width:"100px"}} value={data.gender} onChange={(e) => setProfileData("gender", e.target.value)}>
                                <option value=""></option>
                                <option value="F">{Translate("profileform.gender-f")}</option>
                                <option value="M">{Translate("profileform.gender-m")}</option>
                                <option value="B">{Translate("profileform.gender-b")}</option>
                            </Tag.Select>
                        </Tag.Field>
                        <VisibilityChanger value={data.view_gender} onChange={(v) => setProfileData("view_gender", v)} />
                    </Tag.Row>
                    <Tag.Field className="username">
                        <Tag.Label><T value="profileform.username" /></Tag.Label>
                        <Tag.Input style={{width:"165px"}} value={data.username} onChange={(e) => setProfileData("username", SanitizeUsername(e.target.value))} />
                    </Tag.Field>
                </Tag.Column>
                <Tag.Column className="noFlex">
                    <Tag.Row align="flex-end">
                        <Tag.Field>
                            <Tag.Label><T value="profileform.birthdate" /></Tag.Label>
                            <DateInput className="mb mr" value={data.birthdate} onChange={handleBirthdateChange}/>
                        </Tag.Field>
                        <Tag.Field>
                            <Tag.Label><T value="profileform.age" /></Tag.Label>
                            <Tag.Value style={{width:"60px"}}>{data.age} <T value="profileform.yearsold" /></Tag.Value>
                        </Tag.Field>
                        <VisibilityChanger value={data.view_age} onChange={(v) => setProfileData("view_age", v)} />
                    </Tag.Row>
                    <Tag.Row align="flex-end">
                        <Tag.Field>
                            <Tag.Label><T value="profileform.city" /></Tag.Label>
                            <Tag.Input style={{width:"110px"}} value={data.city} onChange={(e) => setProfileData("city", e.target.value)}/>
                        </Tag.Field>
                        <Tag.Field>
                            <Tag.Label><T value="profileform.state" /></Tag.Label>
                            <Tag.Select style={{width:"60px"}} value={data.state} onChange={(e) => setProfileData("state", e.target.value)}>
                                {statesList.map(s => <option key={s} value={s}>{s}</option>)}
                            </Tag.Select>
                        </Tag.Field>
                        <VisibilityChanger value={data.view_address} onChange={(v) => setProfileData("view_address", v)} />
                    </Tag.Row>
                </Tag.Column>
            </Tag.Row>

            <Tag.Row>
                <Tag.Column style={{marginRight:"50px"}}>
                    <Tag.Row>
                        <Tag.Field style={{flex: 3}}>
                            <Tag.Label><T value="profileform.color" /></Tag.Label>
                            <Tag.Colors>
                                <Tag.Color className={data.color === "#f44336" ? 'selected' : ''} onClick={()=>setProfileData("color", "#f44336")} color={"#f44336"} />
                                <Tag.Color className={data.color === "#ff80ab" ? 'selected' : ''} onClick={()=>setProfileData("color", "#ff80ab")} color={"#ff80ab"} />
                                <Tag.Color className={data.color === "#f18ed7" ? 'selected' : ''} onClick={()=>setProfileData("color", "#f18ed7")} color={"#f18ed7"} />
                                <Tag.Color className={data.color === "#7b1fa2" ? 'selected' : ''} onClick={()=>setProfileData("color", "#7b1fa2")} color={"#7b1fa2"} />
                                <Tag.Color className={data.color === "#ab4fd2" ? 'selected' : ''} onClick={()=>setProfileData("color", "#ab4fd2")} color={"#ab4fd2"} />
                                <Tag.Color className={data.color === "#0011cc" ? 'selected' : ''} onClick={()=>setProfileData("color", "#0011cc")} color={"#0011cc"} />
                                <Tag.Color className={data.color === "#3f51b5" ? 'selected' : ''} onClick={()=>setProfileData("color", "#3f51b5")} color={"#3f51b5"} />
                                <Tag.Color className={data.color === "#64b5f6" ? 'selected' : ''} onClick={()=>setProfileData("color", "#64b5f6")} color={"#64b5f6"} />
                                <Tag.Color className={data.color === "#b2ebf2" ? 'selected' : ''} onClick={()=>setProfileData("color", "#b2ebf2")} color={"#b2ebf2"} />
                                <Tag.Color className={data.color === "#26a69a" ? 'selected' : ''} onClick={()=>setProfileData("color", "#26a69a")} color={"#26a69a"} />
                                <Tag.Color className={data.color === "#886600" ? 'selected' : ''} onClick={()=>setProfileData("color", "#886600")} color={"#886600"} />
                                <Tag.Color className={data.color === "#66bb6a" ? 'selected' : ''} onClick={()=>setProfileData("color", "#66bb6a")} color={"#66bb6a"} />
                                <Tag.Color className={data.color === "#33691e" ? 'selected' : ''} onClick={()=>setProfileData("color", "#33691e")} color={"#33691e"} />
                                <Tag.Color className={data.color === "#d4e157" ? 'selected' : ''} onClick={()=>setProfileData("color", "#d4e157")} color={"#d4e157"} />
                                <Tag.Color className={data.color === "#ffee58" ? 'selected' : ''} onClick={()=>setProfileData("color", "#ffee58")} color={"#ffee58"} />
                                <Tag.Color className={data.color === "#ffca28" ? 'selected' : ''} onClick={()=>setProfileData("color", "#ffca28")} color={"#ffca28"} />
                                <Tag.Color className={data.color === "#ffa726" ? 'selected' : ''} onClick={()=>setProfileData("color", "#ffa726")} color={"#ffa726"} />
                                <Tag.Color className={data.color === "#8d6e63" ? 'selected' : ''} onClick={()=>setProfileData("color", "#8d6e63")} color={"#8d6e63"} />
                                <Tag.Color className={data.color === "#bdbdbd" ? 'selected' : ''} onClick={()=>setProfileData("color", "#bdbdbd")} color={"#bdbdbd"} />
                                <Tag.Color className={data.color === "#f5f5f5" ? 'selected' : ''} onClick={()=>setProfileData("color", "#f5f5f5")} color={"#f5f5f5"} />
                                <Tag.Color className={data.color === "#444444" ? 'selected' : ''} onClick={()=>setProfileData("color", "#444444")} color={"#444444"} />
                                <Tag.Color className={data.color === "#000000" ? 'selected' : ''} onClick={()=>setProfileData("color", "#000000")} color={"#000000"} />
                            </Tag.Colors>
                        </Tag.Field>
                        <Tag.Field style={{marginLeft:'10px', flex: 1}}>
                            <Tag.Label><T value="profileform.language" /></Tag.Label>
                            <Tag.Row>
                                <Tag.LanguageItem onClick={()=>{handleSetLang('pt')}} className={lang === 'pt' ? 'active' : ''} icon="pt" title="Português"/>
                                <Tag.LanguageItem onClick={()=>{handleSetLang('en')}} className={lang === 'en' ? 'active' : ''} icon="en" title="English"/>
                            </Tag.Row>
                        </Tag.Field>
                    </Tag.Row>

                    <Tag.Field>
                        <Tag.Label><T value="profileform.bio" /></Tag.Label>
                        <Tag.TextArea style={{width:"100%", height:"200px"}} value={data.bio} onChange={(e) => setProfileData("bio", e.target.value)}/>
                    </Tag.Field>

                    <Tag.Separator><T value="profileform.resouces" /></Tag.Separator>

                    <Tag.Row>
                        <Tag.Column>
                            <Checkbox className={'variant'} value={data.acceptVoice} onClick={(v) => setProfileData("acceptVoice", v)}><T value="profileform.acceptVoice" /></Checkbox>
                        </Tag.Column>
                        <Tag.Column>
                            <Checkbox className={'variant'} value={data.hasDesktop} onClick={(v) => setProfileData("hasDesktop", v)}><T value="profileform.haspc" /></Checkbox>
                        </Tag.Column>
                    </Tag.Row>
                    <Tag.Row>
                        <Tag.Column>
                            <Checkbox className={'variant'} value={data.acceptVideo} onClick={(v) => setProfileData("acceptVideo", v)}><T value="profileform.acceptVideo" /></Checkbox>
                        </Tag.Column>
                        <Tag.Column>
                            <Checkbox className={'variant'} value={data.hasTablet} onClick={(v) => setProfileData("hasTablet", v)}><T value="profileform.hasTablet" /></Checkbox>
                        </Tag.Column>
                    </Tag.Row>
                    <Tag.Row>
                        <Tag.Column>
                            <Checkbox className={'variant'} value={data.acceptStream} onClick={(v) => setProfileData("acceptStream", v)}><T value="profileform.acceptStream" /></Checkbox>
                        </Tag.Column>
                        <Tag.Column>
                            <Checkbox className={'variant'} value={data.hasPhone} onClick={(v) => setProfileData("hasPhone", v)}><T value="profileform.hasPhone" /></Checkbox>
                        </Tag.Column>
                    </Tag.Row>

                    <Tag.Separator><T value="profileform.contacts" /></Tag.Separator>

                    <Tag.Row align="center">
                        <Tag.Label className='row'><T value="profileform.whatsapp" /></Tag.Label>
                        <Tag.Input style={{width:"200px"}} placeholder="(99) 99999-9999" value={data.whatsapp} onChange={(e) => setProfileData("whatsapp", e.target.value)}/>
                        <VisibilityChanger value={data.view_whatsapp} onChange={(v) => setProfileData("view_whatsapp", v)} />
                    </Tag.Row>
                    <Tag.Row align="center">
                        <Tag.Label className='row'><T value="profileform.telegram" /></Tag.Label>
                        <Tag.Input style={{width:"200px"}} placeholder="@hubrpg" value={data.telegram} onChange={(e) => setProfileData("telegram", e.target.value)}/>
                        <VisibilityChanger value={data.view_telegram} onChange={(v) => setProfileData("view_telegram", v)} />
                    </Tag.Row>
                    <Tag.Row align="center">
                        <Tag.Label className='row'><T value="profileform.facebook" /></Tag.Label>
                        <Tag.Input style={{width:"200px"}} placeholder="/hubrpg" value={data.facebook} onChange={(e) => setProfileData("facebook", e.target.value)}/>
                        <VisibilityChanger value={data.view_facebook} onChange={(v) => setProfileData("view_facebook", v)} />
                    </Tag.Row>
                    <Tag.Row align="center">
                        <Tag.Label className='row'><T value="profileform.instagram" /></Tag.Label>
                        <Tag.Input style={{width:"200px"}} placeholder="@hubrpg" value={data.instagram} onChange={(e) => setProfileData("instagram", e.target.value)}/>
                        <VisibilityChanger value={data.view_instagram} onChange={(v) => setProfileData("view_instagram", v)} />
                    </Tag.Row>
                    <Tag.Row align="center">
                        <Tag.Label className='row'><T value="profileform.twitter" /></Tag.Label>
                        <Tag.Input style={{width:"200px"}} placeholder="@hubrpg" value={data.twitter} onChange={(e) => setProfileData("twitter", e.target.value)}/>
                        <VisibilityChanger value={data.view_twitter} onChange={(v) => setProfileData("view_twitter", v)} />
                    </Tag.Row>
                    <Tag.Row align="center">
                        <Tag.Label className='row'><T value="profileform.discord" /></Tag.Label>
                        <Tag.Input style={{width:"200px"}} placeholder="https://discord.gg/dbBhmUBEhQ" value={data.discord} onChange={(e) => setProfileData("discord", e.target.value)}/>
                        <VisibilityChanger value={data.view_discord} onChange={(v) => setProfileData("view_discord", v)} />
                    </Tag.Row>


                </Tag.Column>
                
                <Tag.Column>
                    <Tag.UserTabs>
                        <Tag.UserTab onClick={()=>{setTabSelected("player")}} className={tabSelected === 'player' ? 'selected' : ''} ><T value="profileform.asPlayer" /></Tag.UserTab>
                        <Tag.UserTab onClick={()=>{setTabSelected("gm")}} className={tabSelected === 'gm' ? 'selected' : ''} ><T value="profileform.asGM" /></Tag.UserTab>
                    </Tag.UserTabs>
                    {tabSelected === 'player' ? <>
                        <Checkbox className={'variant'} value={data.player_acceptInvite} onClick={(v) => setProfileData("player_acceptInvite", v)}><T value="profileform.acceptInviteToPlay" /></Checkbox>
                        <TagSelector 
                            className="variant"
                            includedColor="#006CFF"
                            canExclude={false}
                            canAdd={true}
                            hideSubtitles={true}
                            tagGroup="gamesystems"
                            tagService="system"
                            value={data.player_systems} 
                            onChange={(v) => setProfileData("player_systems", v)}
                        />
                        <TagSelector 
                            className="variant"
                            includedColor="#56B431"
                            canExclude={false}
                            canAdd={true}
                            hideSubtitles={true}
                            tagGroup="contentInclude"
                            tagService="content"
                            value={data.player_contentLikable} 
                            onChange={(v) => setProfileData("player_contentLikable", v)}
                        />
                        <TagSelector 
                            className="variant"
                            includedColor="#ff3333"
                            canExclude={false}
                            canAdd={true}
                            hideSubtitles={true}
                            tagGroup="contentAvoid"
                            tagService="content"
                            value={data.player_contentAvoidable} 
                            onChange={(v) => setProfileData("player_contentAvoidable", v)}
                        />
                        <TagSelector 
                            className="variant"
                            includedColor="#FFB800"
                            canExclude={false}
                            canAdd={true}
                            hideSubtitles={true}
                            tagGroup="tools"
                            tagService="tools"
                            value={data.player_tools} 
                            onChange={(v) => setProfileData("player_tools", v)}
                        />
                    </>:<>
                        <Checkbox className={'variant'} value={data.gm_acceptInvite} onClick={(v) => setProfileData("gm_acceptInvite", v)}><T value="profileform.acceptInviteToGM" /></Checkbox>
                        <TagSelector 
                            className="variant"
                            includedColor="#006CFF"
                            canExclude={false}
                            canAdd={true}
                            hideSubtitles={true}
                            tagGroup="gamesystems"
                            tagService="system"
                            value={data.gm_systems} 
                            onChange={(v) => setProfileData("gm_systems", v)}
                        />
                        <TagSelector 
                            className="variant"
                            includedColor="#56B431"
                            canExclude={false}
                            canAdd={true}
                            hideSubtitles={true}
                            tagGroup="contentInclude"
                            tagService="content"
                            value={data.gm_contentLikable} 
                            onChange={(v) => setProfileData("gm_contentLikable", v)}
                        />
                        <TagSelector 
                            className="variant"
                            includedColor="#ff3333"
                            canExclude={false}
                            canAdd={true}
                            hideSubtitles={true}
                            tagGroup="contentAvoid"
                            tagService="content"
                            value={data.gm_contentAvoidable} 
                            onChange={(v) => setProfileData("gm_contentAvoidable", v)}
                        />
                        <TagSelector 
                            className="variant"
                            includedColor="#FFB800"
                            canExclude={false}
                            canAdd={true}
                            hideSubtitles={true}
                            tagGroup="tools"
                            tagService="tools"
                            value={data.gm_tools} 
                            onChange={(v) => setProfileData("gm_tools", v)}
                        />
                    </>}
                </Tag.Column>
            </Tag.Row>

            <Tag.Column>
                <Tag.Separator><T value="profileform.availability" /></Tag.Separator>
                <Tag.Row align="flex-end">
                    <Tag.Field>
                        <Tag.Label><T value="profileform.weekday" /></Tag.Label>
                        <Tag.Select style={{width:"110px"}} value={availabilityForm.weekday} onChange={(e)=>setAvailabilityData("weekday",e.target.value)}>
                            <option value=""></option>
                            <option value="0">{Translate("date-ddd.0")}</option>
                            <option value="1">{Translate("date-ddd.1")}</option>
                            <option value="2">{Translate("date-ddd.2")}</option>
                            <option value="3">{Translate("date-ddd.3")}</option>
                            <option value="4">{Translate("date-ddd.4")}</option>
                            <option value="5">{Translate("date-ddd.5")}</option>
                            <option value="6">{Translate("date-ddd.6")}</option>
                        </Tag.Select>
                    </Tag.Field>
                    <Tag.Field>
                        <Tag.Label><T value="profileform.timestart" /></Tag.Label>
                        <TimeSelector style={{margin:'0 5px 10px 0'}} value={availabilityForm.timestart} onChange={(v)=>setAvailabilityData("timestart",v)}/>
                    </Tag.Field>
                    <Tag.Field>
                        <Tag.Label><T value="profileform.timeend" /></Tag.Label>
                        <TimeSelector style={{margin:'0 5px 10px 0'}} value={availabilityForm.timeend} onChange={(v)=>setAvailabilityData("timeend",v)}/>
                    </Tag.Field>
                    <Tag.Field>
                        <Tag.Label><T value="profileform.frequency"/></Tag.Label>
                        <Tag.Select style={{width:"120px"}} value={availabilityForm.frequency} onChange={(e)=>setAvailabilityData("frequency",e.target.value)}>
                            <option value=""></option>
                            <option value="1">{Translate("frequencies.1")}</option>
                            <option value="2">{Translate("frequencies.2")}</option>
                            <option value="3">{Translate("frequencies.3")}</option>
                        </Tag.Select>
                    </Tag.Field>
                    <Tag.Field style={{flex: 1}}>
                        <Tag.Label><T value="profileform.obs" /></Tag.Label>
                        <Tag.Input style={{width: '100%'}} value={availabilityForm.note} onChange={(e)=>setAvailabilityData("note",e.target.value)}/>
                    </Tag.Field>
                    {selectedAvailability === null ? <>
                        <Tag.Button color="#0c0" className={isValidAvailability() ? '' : 'disabled'} onClick={handleSaveAvailability}><T value="add" /></Tag.Button>
                    </> : <>
                        <Tag.ButtonIcon color="#f33" icon={"\\F01B4"} onClick={()=>handleRemoveAvailability(selectedAvailability)} title={Translate("delete")} />
                        <Tag.Button color="#29f" className={isValidAvailability() ? '' : 'disabled'} onClick={handleSaveAvailability}><T value="save" /></Tag.Button>
                    </>}
                </Tag.Row>
                <Tag.Availabilities>
                    {availability && availability.length ? availability.map(a => <Tag.TableRow key={a.id} className={selectedAvailability === a.id ? 'selected' : ''} onClick={()=>handleSelectAvailability(a)}>
                        <Tag.TableData style={{width:'115px'}}>{Translate(`date-ddd.${a.weekday}`)}</Tag.TableData>
                        <Tag.TableData style={{width:'65px'}}>{a.timestart}</Tag.TableData>
                        <Tag.TableData style={{width:'65px'}}>{a.timeend}</Tag.TableData>
                        <Tag.TableData style={{width:'125px'}}>{Translate(`frequencies.${a.frequency}`)}</Tag.TableData>
                        <Tag.TableData style={{flex: 1}}>{a.note}</Tag.TableData>
                    </Tag.TableRow>) : <Tag.NoAvailability>
                        <T value="profileform.NoAvailability" />
                    </Tag.NoAvailability>}
                </Tag.Availabilities>
            </Tag.Column>
        </Tag.Content>
        <Tag.FooterRow>
            <Tag.Button className="special" color="#888" onClick={handleBack}><T value="cancel" /></Tag.Button>
            <Tag.Button className="special" color="#0c0" onClick={handleSave}><T value="save" /></Tag.Button>
        </Tag.FooterRow>
    </>
};